import {
  AlertDialogButtons,
  AlertDialogDescription,
  AlertDialogTitle
} from "@/constants/alert-dialog/alert-dialog"
import {
  LoginMethod,
  CredentialDetailsResponseData,
  GooglePickerResp,
  GoogleSheetTokensResponse,
  SelectedDocument,
  CredentialCreateRequestBody
} from "@/types/credentials"
import {
  createCredentials,
  deleteCredential,
  getGoogleTokensFromAuthCode,
  updateCredentials,
} from "@/services/credentialsService"
import dayjs from 'dayjs'
import successStatusIcon from '@/assets/icons/success.svg'
import useDrivePicker from 'react-google-drive-picker'
import { useState, useEffect } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useSelector, useDispatch } from "react-redux"
import { AppDispatch, RootState } from "@/redux"
import { Button } from "@/components/ui/button"
import { useNavigate } from "react-router-dom"
import { AlertDialogButton } from '@/components/custom/AlertDialogButton/AlertDialogButton'
import { CredentialMethod, CredentialAttributeName } from "@/constants/credentials/credential"
import { ButtonVariant } from "@/constants/button/button"
import { getButtonName } from '../helpers/getButtonName'
import { Form } from "@/components/ui/form"
import { TextInputField } from "@/components/custom/InputField/TextInputField"
import { UserRole } from "@/constants/users/userRole"
import { TCS } from "@/constants/TCS/TCS"
import { AlertDialogAction } from "@/components/ui/alert-dialog"
import { showNotification } from "@/services/notificationService"
import { config } from "@/config"
import { useGoogleLogin } from "@react-oauth/google"

const formSchema = z.object({
  nickname: z.string().min(1, {
    message: 'Please give this credential a nickname'
  }),
  googleAuthCode: z.string()
})

export const GoogleSheetCredentialForm = ({
  method,
  loginMethods,
  appId,
  isFormModal,
  credentialDetails,
}: {
  loginMethods: LoginMethod[],
  appId: string,
  method: CredentialMethod,
  isFormModal: boolean,
  credentialDetails?: CredentialDetailsResponseData
}) => {
  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const [connectionType, setConnectionType] = useState<string | undefined>(undefined)
  const { token, userData } = useSelector((state: RootState) => state.user)
  const [openPicker,] = useDrivePicker();
  const [selectedFile, setSelectedFile] = useState<SelectedDocument | undefined>(undefined)
  const [selectedFileId, setSelectedFileId] = useState<string | undefined>(undefined)
  const [tokenResp, setTokenResp] = useState<GoogleSheetTokensResponse | undefined>()
  const isReadOnly = userData?.role === UserRole.USER
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nickname: "",
      googleAuthCode: "",
    },
  })

  const nickName = form.watch('nickname')
  const googleAuthCode = form.watch('googleAuthCode')

  const connectWithGoogle = useGoogleLogin({
    onSuccess: codeResponse => form.setValue('googleAuthCode', codeResponse.code),
    flow: 'auth-code',
    scope: TCS.GOOGLE_AUTH_SCOPE as string
  })

  // Exchange AuthCode with access_token and refresh_token after getting auth code
  useEffect(() => {
    if (googleAuthCode?.length && token) {
      const fetchTokensFromAuthCode = async () => {
        const resp = await dispatch(getGoogleTokensFromAuthCode(token, googleAuthCode))
        if (resp.data) {
          setTokenResp({
            ...resp.data,
            refreshTokenExpireDate: String(dayjs().add(TCS.GOOGLE_SHEET_TOKEN_LIFE_SPAN, 'days').toDate())
          })
        }
      }
      fetchTokensFromAuthCode()
        .catch(err => console.log(err))
    }
  }, [googleAuthCode, token, dispatch])

  // reset GoogleSheet if there is no token response
  useEffect(() => {
    if (!tokenResp?.accessToken || tokenResp.accessToken.length === 0) {
      form.reset({ googleAuthCode: "" })
    }
  }, [tokenResp, form])

  useEffect(() => {
    if (loginMethods?.length && !connectionType) {
      // GoogleSheet supports one login method: OAuth. We set default to first Index
      setConnectionType(loginMethods[0].methodName)
    }
  }, [loginMethods, connectionType])

  const handleOpenPicker = () => {
    // only open Sheet picker if already authenticated with google
    // required access token to open the picker
    const appId =
      config.env === "production"
        ? (TCS.GOOGLE_APP_ID_PRODUCTION as string)
        : (TCS.GOOGLE_APP_ID_SANDBOX as string);

    if (tokenResp?.accessToken?.length) {
      openPicker({
        clientId: config.env === 'production' ? TCS.GOOGLE_AUTH_CLIENT_ID_PRODUCTION as string : TCS.GOOGLE_AUTH_CLIENT_ID_SANDBOX as string,
        developerKey: config.googleApiKey,
        viewId: "SPREADSHEETS",
        token: tokenResp.accessToken,
        appId: appId,
        supportDrives: true,
        multiselect: false,
        callbackFunction: (data) => {
          if (data.action === 'cancel') {
            showNotification({ data: undefined, err: '⚠️ Cancelled picking Google Sheet.' }, '', {})
          } else if (data.action === 'picked') {
            const resp = data as unknown as GooglePickerResp
            if (resp.docs[0]) {
              setSelectedFile(resp.docs[0])
              setSelectedFileId(resp.docs[0].id)
            }
          }
        },
      })
    }
  }

  const onFormSubmit = async (
    values: z.infer<typeof formSchema>
  ) => {
    if (!token || !tokenResp || !selectedFileId) return
    const { nickname } = values
    const loginMethodCode = loginMethods.find(lm => lm.methodName === connectionType)?.methodName
    if (!loginMethodCode) return
    const body: CredentialCreateRequestBody = {
      appId,
      loginMethodCode,
      nickname,
      credentialValues: [
        {
          credentialAttributeCode: CredentialAttributeName.REFRESH_TOKEN,
          attributeValue: tokenResp.refreshToken,
        },
        {
          credentialAttributeCode: CredentialAttributeName.ACCESS_TOKEN,
          attributeValue: tokenResp.accessToken,
        },
        {
          credentialAttributeCode: CredentialAttributeName.EXPIRATION_DATE,
          attributeValue: tokenResp.expiryDate,
        },
        {
          credentialAttributeCode: CredentialAttributeName.RERESH_TOKEN_EXPIRE_DATE,
          attributeValue: tokenResp.refreshTokenExpireDate ?? String(dayjs().add(TCS.GOOGLE_SHEET_TOKEN_LIFE_SPAN, 'days').toDate()),
        },
        {
          credentialAttributeCode: CredentialAttributeName.SPREADSHEET_ID,
          attributeValue: selectedFileId,
        },
      ]
    }
    if (method === CredentialMethod.CREATE) {
      const resp = await dispatch(createCredentials(token, body))
      if (resp.data) onFormSubmitSuccess()
    }
    if (method === CredentialMethod.EDIT && credentialDetails) {
      const resp = await dispatch(updateCredentials(token, credentialDetails.id, body))
      if (resp.data) onFormSubmitSuccess()
    }
  }

  const onFormSubmitSuccess = () => {
    form.reset({ nickname: "", googleAuthCode: "" })
    if (!isFormModal) {
      navigate('/credentials')
    }
  }

  useEffect(() => {
    if (method === CredentialMethod.EDIT && credentialDetails && loginMethods) {
      const nickname = credentialDetails.nickname
      const loginMethod = loginMethods.find(
        method => method.methodName === credentialDetails.loginMethodCode
      )
      if (loginMethod) {
        setConnectionType(loginMethod.methodName)
        form.setValue('nickname', nickname)
        const tokenRespFromTCS: GoogleSheetTokensResponse = { accessToken: '', refreshToken: '', expiryDate: '' }
        credentialDetails.credentialValues.forEach(cValue => {
          if (cValue.credentialAttributeCode === CredentialAttributeName.TOKEN) {
            form.setValue('googleAuthCode', cValue.attributeValue)
          } else if (cValue.credentialAttributeCode === CredentialAttributeName.ACCESS_TOKEN) {
            tokenRespFromTCS.accessToken = cValue.attributeValue
          } else if (cValue.credentialAttributeCode === CredentialAttributeName.REFRESH_TOKEN) {
            tokenRespFromTCS.refreshToken = cValue.attributeValue
          } else if (cValue.credentialAttributeCode === CredentialAttributeName.EXPIRATION_DATE) {
            tokenRespFromTCS.expiryDate = cValue.attributeValue
          } else if (cValue.credentialAttributeCode === CredentialAttributeName.RERESH_TOKEN_EXPIRE_DATE) {
            tokenRespFromTCS.refreshTokenExpireDate = cValue.attributeValue
          } else if (cValue.credentialAttributeCode === CredentialAttributeName.SPREADSHEET_ID) {
            setSelectedFileId(cValue.attributeValue)
            setSelectedFile({
              iconUrl: credentialDetails.app.logo,
              name: 'Preview',
              url: `https://docs.google.com/spreadsheets/d/${cValue.attributeValue}/edit`
            } as SelectedDocument)
          }
        })
        setTokenResp(tokenRespFromTCS)
      }
    }
  }, [method, credentialDetails, form, loginMethods])

  const isSubmitButtonDisabled = () => {
    return !tokenResp?.accessToken ||
      tokenResp.accessToken.length === 0 ||
      nickName?.length === 0 ||
      !selectedFileId
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onFormSubmit)}
        className="space-y-8 flex flex-col items-start"
      >
        <TextInputField
          form={form}
          schema={formSchema}
          name="nickname"
          label="Nickname"
          placeholder="GoogleSheet - Output 2025"
          error={!!form.formState.errors.nickname}
          required={true}
          description="Give a unique name to identify this credential"
          isDisabled={isReadOnly}
        />
        <div className="flex flex-col items-start">
          <p className="text-base font-semibold text-primary mb-2" >
            Connect with Google
          </p>
          <div className="flex flex-row items-center">
            <Button
              type="button"
              className="font-semibold w-60"
              onClick={() => connectWithGoogle()}
              disabled={!!tokenResp?.accessToken}
              variant={ButtonVariant.SECONDARY}
            >
              {method === CredentialMethod.EDIT ? 'Re-connect' : 'Connect'} Google Account
            </Button>
            {!!tokenResp?.accessToken && <img className="successStatusIcon ml-3" src={successStatusIcon} />}
          </div>
        </div>
        <div className="flex items-center mb-2">
          <div className="flex flex-col items-start">
            <p className="text-base font-semibold text-primary mb-2" >
              Select Google Sheet from your Drive
            </p>
            <div className="flex items-center">
              <Button
                type="button"
                className="font-semibold w-60 mr-2"
                onClick={() => handleOpenPicker()}
                variant={ButtonVariant.SECONDARY}
                disabled={tokenResp?.accessToken?.length === 0}
              >
                {selectedFileId ? 'Change' : 'Select'} Google Sheet
              </Button>
              {selectedFile ? (
                <div className="flex items-center">
                  <img className="h-4 mr-2" src={selectedFile?.iconUrl} />
                  <a
                    href={selectedFile.url}
                    target="_blank"
                    className="text-sm text-accent underline text-left"
                  >
                    {selectedFile.name}
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {!isReadOnly && (
          <div className='w-[400px] flex flex-row items-center justify-between'>
            {/* <Button
              type="submit"
              className="mb-2 font-semibold w-60"
              disabled={googleAuthCode.length === 0 || nickName?.length === 0}
            >
              {getButtonName(method)}
            </Button> */}
            {isFormModal ? (
              <AlertDialogAction asChild>
                <Button
                  type="submit"
                  className="mb-2 font-semibold w-60 mr-6"
                  disabled={isSubmitButtonDisabled()}
                >
                  {getButtonName(method)}
                </Button>
              </AlertDialogAction>
            ) : (
              <Button
                type="submit"
                className="mb-2 font-semibold w-60 mr-6"
                disabled={isSubmitButtonDisabled()}
              >
                {getButtonName(method)}
              </Button>
            )}
            {method === CredentialMethod.EDIT && (
              <AlertDialogButton
                actionButtonText={AlertDialogButtons.DELETE}
                dialogTitle={AlertDialogTitle.DELETE_CREDENTIAL}
                dialogSubtitle={AlertDialogDescription.DELETE_CREDENTIAL}
                onClick={() => { credentialDetails && token && dispatch(deleteCredential(token, credentialDetails.id)) }}
                variant={ButtonVariant.LINK}
                actionButtonVariant={ButtonVariant.DESTRUCTIVE}
                isTableActionButton={false}
              />
            )}
          </div>
        )}
      </form>
    </Form >
  )
}
